import { expiresInText, expiryReminderSeverity } from '@constants/expiry';
import { PAGE_TITLES, ROUTE_PATHS } from '@constants/routes';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton, Stack } from '@mui/material';
import { useGetInstallationQuery } from '@store/api/installationEndpoints';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DISMISSED_KEY = 'certificateReminderDismissed';
const HOUR_MS = 60 * 60 * 1000;

// The full width of the content column, padding and border included. The signed-in layout has no
// CssBaseline, so a width of 100% left the banner 34 px wider than the column, and at 390 and 600
// every page scrolled sideways under it.
const BANNER_SX = { width: '100%', boxSizing: 'border-box' } as const;

// Storage can be unavailable (a private window, blocked site data). The banner then simply
// comes back on the next page load, which is the safe way for it to fail.
const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (value: string) => {
  try {
    localStorage.setItem(DISMISSED_KEY, value);
  } catch {
    // See readDismissed.
  }
};

/**
 * The server certificate's expiry, above every page.
 *
 * An expiry stops every device at once, offline ones included, and the Enrolment page is the only
 * other place that says so, which nobody opens until something is wrong. So from 60 days out the
 * reminder follows the administrator around. Each reminder point (60, 30, 14, 7 days, decided by
 * the server) can be dismissed, and the next one brings it back; a renewed certificate starts
 * over because its serial is part of what was dismissed. A certificate that has lapsed cannot be
 * dismissed at all, because by then every device has stopped.
 *
 * Not shown on the Enrolment page itself, which reports the certificate in full.
 */
const CertificateBanner: React.FC = () => {
  // Hourly, like the server's own re-check, so a panel left open for days keeps counting down
  // and still notices the certificate lapse.
  const { data: installation } = useGetInstallationQuery(undefined, { pollingInterval: HOUR_MS });
  const [dismissed, setDismissed] = useState(readDismissed);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const certificate = installation?.certificate;

  if (!certificate || pathname === ROUTE_PATHS.ACTIVATION) return null;

  const openEnrolment = (
    <Button color='inherit' size='small' onClick={() => navigate(ROUTE_PATHS.ACTIVATION)}>
      {PAGE_TITLES[ROUTE_PATHS.ACTIVATION]}
    </Button>
  );

  if (!certificate.installed) {
    // Never installed: nothing has stopped yet, and the Enrolment page is where setup starts.
    if (!certificate.stored) return null;

    return (
      <Alert severity='error' action={openEnrolment} sx={BANNER_SX}>
        The server certificate is no longer in force, so no device can be licensed until a renewed
        certificate is installed. {certificate.problem}
      </Alert>
    );
  }

  const { expiryReminderDays, expiresInDays, notAfter, certSerial } = certificate;

  if (expiryReminderDays === undefined || expiresInDays === undefined || !notAfter) return null;

  const reminderKey = `${certSerial}:${expiryReminderDays}`;

  if (dismissed === reminderKey) return null;

  const dismiss = () => {
    writeDismissed(reminderKey);
    setDismissed(reminderKey);
  };

  return (
    <Alert
      severity={expiryReminderSeverity(expiryReminderDays)}
      sx={BANNER_SX}
      action={
        <Stack direction='row' alignItems='center'>
          {openEnrolment}
          <IconButton aria-label='Dismiss until the next reminder' color='inherit' size='small' onClick={dismiss}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Stack>
      }
    >
      The server certificate expires {expiresInText(expiresInDays)}, on{' '}
      <strong>{new Date(notAfter).toFormattedString()}</strong>. Every device stops on that date,
      including devices that are offline. Download an enrollment request and ask CDMVision for a
      renewal before then.
    </Alert>
  );
};

export default CertificateBanner;
